/**
 * `<ConversationHealthBar>` — model-derived conversation-health signal.
 *
 * REPLACES the old `<TranscriptSentimentBar>` (a keyword bag-of-words over the
 * raw transcript — fake, and it contradicted the verdict). This renders the
 * REAL signal the model emits in the `## Conversation Health` section of the
 * post-interview analysis: the HEALTH of the interviewer↔candidate exchange —
 * warmth, two-way engagement, how openly hard topics were handled.
 *
 * Bias posture (on-thesis): this is the INTERACTION dynamic, NOT the candidate's
 * likability / culture-fit / personality (those encode affinity bias — see the
 * backend prompt's bias framing). The label is "Conversation tone / Rapport".
 *
 * Renders nothing when the analysis has no parseable signal (older analyses
 * generated before the signal existed) — caller passes the raw analysis markdown.
 *
 * Final label + visual treatment are the HoD's call; this proposes
 * "Conversation tone / Rapport" with a single score bar + qualitative read.
 */
import type { JSX } from "react";
import {
  parseConversationHealth,
  healthColor,
} from "@/lib/interview-analysis/parsers";

interface ConversationHealthBarProps {
  /** Full raw post-interview analysis markdown. */
  analysis: string;
}

export function ConversationHealthBar({
  analysis,
}: ConversationHealthBarProps): JSX.Element | null {
  const health = parseConversationHealth(analysis);
  if (!health) return null;

  const { rating, score, read } = health;
  const color = healthColor(rating);

  return (
    <div className="mb-3.5" data-testid="conversation-health-bar">
      {/* Label — 9px uppercase, matches the prior bar's label treatment. */}
      <div
        className="flex items-center justify-between text-text-tertiary"
        style={{ marginBottom: "7px" }}
      >
        <span
          style={{
            fontSize: "9px",
            textTransform: "uppercase",
            letterSpacing: ".8px",
            fontWeight: 700,
          }}
        >
          Conversation Tone / Rapport
        </span>
        <span
          data-testid="conversation-health-rating"
          style={{ fontSize: "10px", fontWeight: 700, color }}
        >
          {rating} ({score})
        </span>
      </div>

      {/* Single score bar — fill proportional to the model score. */}
      <div
        className="flex w-full overflow-hidden bg-slate-600"
        style={{ height: "9px", borderRadius: "5px", margin: "8px 0" }}
        aria-label={`Conversation health: ${rating}, ${score} of 100`}
        role="img"
      >
        <div
          style={{
            width: `${score}%`,
            height: "100%",
            backgroundColor: color,
            transition: "width .5s ease",
          }}
          data-testid="conversation-health-fill"
          data-width={score}
        />
      </div>

      {/* Qualitative read — the model's description of the interaction dynamic. */}
      {read ? (
        <p
          className="text-text-tertiary"
          style={{ fontSize: "11px", lineHeight: 1.45, marginTop: "2px" }}
          data-testid="conversation-health-read"
        >
          {read}
        </p>
      ) : null}
    </div>
  );
}
